import { useState, useEffect, useCallback } from "react";
import ApiService from "../../utils/ApiService";

function useCategoryData(mode, categoryData, user, restaurantId, setIsSetMenu) {
  const [categories, setCategories] = useState([]);
  const [categoryDetails, setCategoryDetails] = useState({});
  const [categoryName, setCategoryName] = useState("");
  const [options, setOptions] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [actionMode, setActionMode] = useState(mode);
  const [isInitialEditMode, setIsInitialEditMode] = useState(false);
  const [hasMultipleOptions, setHasMultipleOptions] = useState(false);

  // Initialize default row
  const getDefaultRow = () => ({
    id: Date.now().toString(), // Ensure id is always a string and unique
    name: "",
    weight: "",
    isOptionRemoved: "No",
  });

  const fetchCategorySuggestions = useCallback(async () => {
    const token = user?.token?.token;
    if (!Boolean(token)) return;
    try {
      const response = await ApiService.GET_CATEGORY_SUGGESTION({}, user.token.token);
      if (response?.payload?.records) {
        const fetchedCategories = response.payload.records.map((cat) => ({
          id: cat.id,
          name: cat.name,
          image: "https://via.placeholder.com/150",
        }));
        setCategories(fetchedCategories);
        setOptions(
          fetchedCategories.map((cat) => ({
            value: cat.name,
          }))
        );
      } else {
        console.error("Failed to fetch category suggestions: ", response);
      }
    } catch (error) {
      console.error("Error fetching categories: ", error);
    }
  }, []);

  const fetchCategoryDetails = useCallback(
    async (categoryId, restaurantId, token) => {
      if (!categoryId) return;

      try {
        const response = await ApiService.GET_SPECIFIC_CATEGORY_DETAILS(
          { categoryId, restaurantId },
          token
        );
        if (response?.payload?.record) {
          const record = response.payload.record;
          // const backendBaseURL = process.env.REACT_APP_IMAGE_BASE_URL;
          // const imageUrl = `${backendBaseURL}${record.image.replace(
          //   /\\/g,
          //   "/"
          // )}`;
          const imageUrl = `${record.image.replace(
            /\\/g,
            "/"
          )}`;
          setHasMultipleOptions(record.hasMultipleOptions);
          setCategoryDetails(record);
          setCategoryName(record.name);
          setFileList(imageUrl ? [{ url: imageUrl }] : []);
          if (record.options && record.options.length > 0) {
            setRows(
              record.options.map((option) => ({
                id: option._id,
                name: option.name,
                weight: option.weight.toString(),
              }))
            );
            setOriginalRows(
              record.options.map((option) => ({
                id: option._id,
                name: option.name,
                weight: option.weight.toString(),
                isOptionRemoved: "No",
              }))
            );
          }
        } else {
          console.error("Failed to fetch category details: ", response);
        }
      } catch (error) {
        console.error("Error fetching category details: ", error);
      }
    },
    []
  );

  // Reset rows to initial state
  const resetRows = useCallback(() => {
    setRows([]);
    setOriginalRows([]);
  }, []);

  // Modified clearSuggestions to use resetRows
  const clearSuggestions = useCallback(() => {
    setCategoryName("");
    setIsSetMenu(false);
    setOptions(categories.map((cat) => ({ value: cat.name })));
    setFileList([]);
    resetRows();
    setActionMode(mode);
  }, [categories, mode, resetRows]);

  const handleCategorySearch = useCallback(
    (value) => {
      setCategoryName(value);

      if (value) {
        const filteredOptions = categories
          .filter((cat) => cat.name.toLowerCase().includes(value.toLowerCase()))
          .map((cat) => ({ value: cat.name }));
        setOptions(filteredOptions);

        const matchingCategory = categories.find(
          (cat) => cat.name.toLowerCase() === value.toLowerCase()
        );

        if (!matchingCategory) {
          setActionMode("add");
          setIsInitialEditMode(false);
          //setFileList([]);
          //resetRows(); // Use resetRows instead of direct setting
        }
      } else {
        clearSuggestions();
      }
    },
    [categories, clearSuggestions, resetRows]
  );

  const handleCategorySelect = useCallback(
    async (selectedValue) => {
      const selectedCategory = categories.find(
        (cat) => cat.name.toLowerCase() === selectedValue.toLowerCase()
      );
      if (selectedCategory && user?.token?.token) {
        setActionMode("edit");
        setIsInitialEditMode(false); // Allow searching since mode was switched
        await fetchCategoryDetails(
          selectedCategory.id,
          restaurantId,
          user.token.token
        );
      }
    },
    [categories, fetchCategoryDetails, restaurantId, user]
  );

  // Initialize default state when component mounts
  useEffect(() => {
    resetRows();
  }, [resetRows]);

  useEffect(() => {
    const initializeData = async () => {
      const token = user?.token?.token;
      if (mode === "add") {
          await fetchCategorySuggestions();
        }
        if (mode === "edit" && categoryData?.id) {
          await fetchCategoryDetails(categoryData.id, restaurantId, token);
        }
      setActionMode(mode);
    };

    initializeData();
  }, [mode, categoryData, fetchCategorySuggestions, fetchCategoryDetails]);

  return {
    categoryDetails,
    setCategoryDetails,
    categoryName,
    setCategoryName,
    categories,
    options,
    fileList,
    setFileList,
    rows,
    setRows,
    originalRows,
    actionMode,
    isInitialEditMode,
    setIsInitialEditMode,
    setActionMode,
    fetchCategorySuggestions,
    fetchCategoryDetails,
    handleCategorySearch,
    handleCategorySelect, // Add this
    clearSuggestions,
    resetRows, // Export resetRows
    hasMultipleOptions,
    setHasMultipleOptions
  };
}

export default useCategoryData;
